import React, { useState } from 'react';
import { Btn, Input, Select, SectionCard, Stat, StatRow } from '../../components/UI';
import { api } from '../../utils/api';

// HB Way of Selling v1.1 — steps and confirmations, status and flags, value (W1).
// The stage is never set here: it is the highest stage reached by a confirmed step.
// Gate 1 / Gate 2 are confirmed per role track in Needs / Design; every other step here, in any order.
// Rules mirror services/basecampWayOfSelling.js (server) — the server's figures are the ones stored.

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB').replace(/\//g, '.') : '');
const eur = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('en-GB')} EUR`);
const numOrNull = (v) => (v === '' || v == null || isNaN(Number(v)) ? null : Number(v));

// Same rule as the server's valueOf: nothing to price → no value.
function valueOf(c) {
  const g = numOrNull(c.groups), d = numOrNull(c.daysPerGroup);
  const days = g != null && d != null ? g * d : null;
  if (days == null && numOrNull(c.idrCount) == null && numOrNull(c.otherEur) == null) return null;
  return (days || 0) * (numOrNull(c.dayPriceEur) || 0) + (numOrNull(c.idrCount) || 0) * (numOrNull(c.idrPriceEur) || 0) + (numOrNull(c.otherEur) || 0);
}

const STATUS_STYLE = {
  open: { bg: 'var(--teal-pale)', color: 'var(--teal)' },
  'won — delivering': { bg: 'var(--success-pale)', color: 'var(--success)' },
  completed: { bg: 'var(--success-pale)', color: 'var(--success)' },
};

export function StatusChip({ status }) {
  const c = STATUS_STYLE[status] || { bg: 'var(--canvas-warm)', color: 'var(--ink-soft)' };
  return <span style={{ ...chipStyle, background: c.bg, color: c.color }}>{status}</span>;
}

// K · overdue · 30+ — shown on the workspace and on the project card.
export function FlagChips({ flags }) {
  if (!flags) return null;
  const items = [
    flags.k && ['K', 'Gate 1 confirmed without the Checkpoint (mentor and access)', '#e0f2fe', '#075985'],
    flags.overdue && ['overdue', 'The next-step date has passed', '#fde2e1', '#b42318'],
    flags.stale && ['30+ days', 'No change for more than 30 days', '#fff2cc', '#92400e'],
  ].filter(Boolean);
  return items.map(([label, title, bg, color]) => (
    <span key={label} title={title} style={{ ...chipStyle, background: bg, color }}>{label}</span>
  ));
}

export default function WayOfSelling({ projectId, wos, lostReasons, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [openCode, setOpenCode] = useState(null);
  const [form, setForm] = useState({ date: today(), note: '' });
  const c0 = wos.commercials || {};
  const [comm, setComm] = useState({
    groups: c0.groups ?? '', daysPerGroup: c0.daysPerGroup ?? '', dayPriceEur: c0.dayPriceEur ?? '',
    idrCount: c0.idrCount ?? '', idrPriceEur: c0.idrPriceEur ?? '', otherEur: c0.otherEur ?? '',
    chancePct: c0.chancePct ?? '', nextStepDate: wos.nextStepDate ? String(wos.nextStepDate).slice(0, 10) : '',
  });
  const o0 = wos.outcome || {};
  const [out, setOut] = useState({ outcome: o0.outcome || '', reasonId: o0.reasonId || '', note: o0.note || '' });

  async function run(fn) {
    setBusy(true);
    try { await fn(); onChanged(); } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  const confirmStep = (code) => run(async () => {
    await api.explore.setStep(projectId, code, { confirmed: true, date: form.date || today(), note: form.note.trim() || null });
    setOpenCode(null);
  });
  const withdraw = (s) => {
    const warn = s.code === 'gate4' ? '\n\nThe project is no longer won; a registry proposal still waiting is withdrawn.' : '';
    // eslint-disable-next-line no-alert
    if (window.confirm(`Withdraw the confirmation of “${s.label}”? The stage follows the highest step still confirmed.${warn}`))
      run(() => api.explore.setStep(projectId, s.code, { confirmed: false }));
  };
  const saveCommercials = () => run(() => api.explore.updateProject(projectId, {
    groups: comm.groups, daysPerGroup: comm.daysPerGroup, dayPriceEur: comm.dayPriceEur,
    idrCount: comm.idrCount, idrPriceEur: comm.idrPriceEur, otherEur: comm.otherEur,
    chancePct: comm.chancePct, nextStepDate: comm.nextStepDate || null,
  }));
  const saveOutcome = () => {
    if (out.outcome === 'lost' && !out.reasonId) { alert('Choose the reason it was lost — it matters more to the network than the win.'); return; }
    run(() => api.explore.setOutcome(projectId, {
      outcome: out.outcome || null, reasonId: out.outcome === 'lost' ? Number(out.reasonId) : null, note: out.note.trim() || null,
    }));
  };

  // Live preview of the value while typing (the server stores the same figures on Save).
  const g = numOrNull(comm.groups), d = numOrNull(comm.daysPerGroup);
  const days = g != null && d != null ? g * d : null;
  // A priced proposal is the deal value; the figures below stay the estimate until there is one.
  const estimate = valueOf(comm);
  const fromProposal = c0.proposalValue != null;
  const value = fromProposal ? c0.proposalValue : estimate;
  const chance = numOrNull(comm.chancePct);
  const prob = chance != null ? chance : (c0.stageDefault ?? 0);
  const weighted = value == null ? null : Math.round(value * prob / 100);

  return (
    <div style={{ marginTop: 16 }}>
      <SectionCard
        title="HB Way of Selling — steps and confirmations"
        action={<><StatusChip status={wos.status} /><FlagChips flags={wos.flags} /></>}
        hint="The stage is the highest stage reached by a confirmed step — steps can be confirmed in any order. Gate 1 and Gate 2 are confirmed in Needs and Design."
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={stepHeadStyle}>
            <span style={{ width: 20, flexShrink: 0 }} />
            <span style={{ flex: '1 1 0', minWidth: 0 }}>Step</span>
            <span style={{ flex: '1.2 1 0', minWidth: 0 }}>Confirmation to move on</span>
            <span style={{ width: 110, flexShrink: 0, textAlign: 'right' }}>Confirmed?</span>
          </div>
          {wos.steps.map((s, i) => (
            <div key={s.code} style={{ ...stepRowStyle, borderBottom: i === wos.steps.length - 1 ? 'none' : stepRowStyle.borderBottom }}>
              <span style={{ width: 20, flexShrink: 0, fontWeight: 700, color: s.confirmed ? 'var(--success)' : 'var(--ink-faint)' }}>{s.confirmed ? '✓' : '○'}</span>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--ink)' }}>
                  {s.label}
                  {s.leadsTo == null && <span style={{ fontSize: '0.7rem', color: 'var(--ink-faint)', fontWeight: 400 }}> · no stage</span>}
                </div>
                {s.confirmed && (
                  <div style={{ fontSize: '0.74rem', color: 'var(--ink-faint)', marginTop: 1 }}>
                    {fmtDate(s.date)}{s.by ? ` · ${s.by}` : ''}{s.note ? ` · ${s.note}` : ''}
                  </div>
                )}
              </div>
              <div style={{ flex: '1.2 1 0', minWidth: 0, fontSize: '0.86rem', color: 'var(--body-ink)' }}>
                {s.confirmation}
                {openCode === s.code && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, padding: 10, background: 'var(--field)', border: '1px solid var(--field-line)', borderRadius: 'var(--radius-md)' }}>
                    <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={{ maxWidth: 160 }} />
                    <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Note (optional)" style={{ flex: 1, minWidth: 160 }} />
                    <Btn size="sm" variant="ghost" onClick={() => setOpenCode(null)}>Cancel</Btn>
                    <Btn size="sm" loading={busy} onClick={() => confirmStep(s.code)}>Confirm</Btn>
                  </div>
                )}
              </div>
              <div style={{ width: 110, flexShrink: 0, fontSize: '0.78rem', paddingTop: 1, textAlign: 'right' }}>
                {s.trackGate
                  ? <span style={{ color: 'var(--ink-faint)' }}>in {s.trackGate === 1 ? 'Needs' : 'Design'}</span>
                  : s.confirmed
                    ? <Btn variant="link" disabled={busy} onClick={() => withdraw(s)} style={{ fontSize: 'inherit', color: 'var(--ink-soft)' }}>Withdraw</Btn>
                    : openCode !== s.code && <Btn size="sm" variant="outline" disabled={busy} onClick={() => { setOpenCode(s.code); setForm({ date: today(), note: '' }); }} style={{ padding: '3px 12px', fontSize: '0.78rem' }}>Confirm…</Btn>}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Value — groups × days per group × day price + IDR × IDR price + other */}
      <SectionCard title="Value">
        <StatRow min={130}>
          <Stat quiet value={days ?? '—'} label="Total training days" />
          <Stat quiet value={eur(value)} label="Value" sub={fromProposal ? `from the proposal · estimate ${eur(estimate)}` : undefined} />
          <Stat quiet value={`${prob} %`} label="Probability" sub={chance != null ? 'your estimate' : 'stage default'} />
          <Stat quiet value={eur(weighted)} label="Weighted value" />
        </StatRow>
        <div style={gridStyle}>
          <Field label="Groups"><Input type="number" min="0" value={comm.groups} onChange={e => setComm(x => ({ ...x, groups: e.target.value }))} /></Field>
          <Field label="Training days per group"><Input type="number" min="0" step="0.5" value={comm.daysPerGroup} onChange={e => setComm(x => ({ ...x, daysPerGroup: e.target.value }))} /></Field>
          <Field label={`Training day price (EUR)${c0.territoryDayPrice != null ? ` · territory ${c0.territoryDayPrice.toLocaleString('en-GB')}` : ''}`}>
            <Input type="number" min="0" value={comm.dayPriceEur} placeholder={c0.territoryDayPrice != null ? String(c0.territoryDayPrice) : ''} onChange={e => setComm(x => ({ ...x, dayPriceEur: e.target.value }))} />
          </Field>
          <Field label="HB Compass reports (IDR)"><Input type="number" min="0" value={comm.idrCount} onChange={e => setComm(x => ({ ...x, idrCount: e.target.value }))} /></Field>
          <Field label="Report price (EUR)"><Input type="number" min="0" value={comm.idrPriceEur} onChange={e => setComm(x => ({ ...x, idrPriceEur: e.target.value }))} /></Field>
          <Field label="Other items (EUR)"><Input type="number" min="0" value={comm.otherEur} onChange={e => setComm(x => ({ ...x, otherEur: e.target.value }))} /></Field>
          <Field label="Chance of winning (%) — your estimate"><Input type="number" min="0" max="100" value={comm.chancePct} placeholder={`stage default ${c0.stageDefault ?? 0}`} onChange={e => setComm(x => ({ ...x, chancePct: e.target.value }))} /></Field>
          <Field label="Next step date"><Input type="date" value={comm.nextStepDate} onChange={e => setComm(x => ({ ...x, nextStepDate: e.target.value }))} /></Field>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <Btn size="sm" loading={busy} onClick={saveCommercials}>Save</Btn>
        </div>
      </SectionCard>

      {/* Outcome — sits beside the stage; it overrides the status, never the stage */}
      <SectionCard title="Outcome (if the opportunity stops)">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select value={out.outcome} onChange={e => setOut(x => ({ ...x, outcome: e.target.value }))} style={{ maxWidth: 190 }}>
            <option value="">— still open —</option>
            {(wos.outcomes || []).map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
          {out.outcome === 'lost' && (
            <Select value={out.reasonId} onChange={e => setOut(x => ({ ...x, reasonId: e.target.value }))} style={{ maxWidth: 240 }}>
              <option value="">Reason…</option>
              {(lostReasons || []).map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </Select>
          )}
          {out.outcome && <Input value={out.note} onChange={e => setOut(x => ({ ...x, note: e.target.value }))} placeholder="Do we come back, and when?" style={{ flex: 1, minWidth: 180 }} />}
          <Btn size="sm" variant="outline" loading={busy} onClick={saveOutcome} style={{ marginLeft: 'auto' }}>Save outcome</Btn>
        </div>
        {o0.outcome && <div style={{ fontSize: '0.74rem', color: 'var(--ink-faint)', marginTop: 6 }}>Recorded {fmtDate(o0.date)}.</div>}
      </SectionCard>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.76rem', fontWeight: 700, color: 'var(--body-ink)' }}>
      {label}
      {children}
    </label>
  );
}

const chipStyle = { display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' };
const stepHeadStyle = { display: 'flex', gap: 12, padding: '8px 0', borderBottom: '2px solid var(--ink-mid)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' };
const stepRowStyle = { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid var(--line)' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px 22px' };
